/**
 * Confirms a script restore from an older revision in the per-app git
 * repo. Mirrors the shape of `SaveCommitDialog` but the framing is
 * different: we're showing the operator WHAT they're rolling back to,
 * and warning them when the older revision's English plan also
 * differs from current (because we restore script-only, by design).
 *
 * Why a separate component (vs reusing SaveCommitDialog):
 *   - Distinct "restoring FROM" block at the top (phase pill + sha +
 *     date + original message)
 *   - Plan-divergence callout that triggers a small extra fetch
 *   - Pre-filled commit message (`restore: <phase> @ <sha>`)
 *
 * Restore is script-only. The yaml/plan-divergence detection uses a
 * direct text compare between the revision's committed yaml and the
 * current commit's yaml — no deserializer needed. Best-effort: if
 * the comparison fetch fails, the dialog stays usable and just omits
 * the warning.
 */
import { useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/ui'
import { testCasesAPI, type CommitPhase, type TestCaseHistoryEntry } from '@/lib/api'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatLocalDateTime } from '@/lib/utils'

const PHASE_LABEL: Record<CommitPhase, string> = {
  planner: 'Planner',
  generator: 'Generator',
  executor: 'Executor',
  healer: 'Healer',
  system: 'System',
  'manual-anon': 'Manual',
  manual: 'Manual',
}

export interface RestoreCommitDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** TestCase id — used to fetch the current commit's yaml for the
   * divergence check. */
  testCaseId: string
  /** The revision being restored (from the history list row). */
  revision: TestCaseHistoryEntry
  /** Current (HEAD) sha — used to fetch current yaml for divergence
   * compare. HistoryPanel always has this on hand as `entries[0].fullSha`. */
  currentSha: string
  /** Pre-filled author from the signed-in user (read-only). */
  author: { name: string; email: string }
  /** Parent owns the actual API call + error surfacing. Resolves on
   * success (and the dialog closes). */
  onSubmit: (commit: { message: string; details?: string }) => Promise<void>
  /** Disables Restore while the request is in flight. */
  restoring?: boolean
}

export function RestoreCommitDialog({
  open,
  onOpenChange,
  testCaseId,
  revision,
  currentSha,
  author,
  onSubmit,
  restoring = false,
}: RestoreCommitDialogProps) {
  const tz = useUserTimezone()
  const [message, setMessage] = useState('')
  const [details, setDetails] = useState('')
  // null = unknown / fetch in flight, false = plans match, true = differ.
  const [planDiverged, setPlanDiverged] = useState<boolean | null>(null)

  useEffect(() => {
    if (!open) return
    // Pre-fill commit message every time the dialog opens for a new
    // revision so the operator gets a fresh, contextual default.
    setMessage(`restore: ${revision.phase} @ ${revision.sha}`)
    setDetails('')
    setPlanDiverged(null)

    let cancelled = false
    Promise.all([
      testCasesAPI.getRevision(testCaseId, revision.fullSha),
      testCasesAPI.getRevision(testCaseId, currentSha),
    ])
      .then(([selected, current]) => {
        if (cancelled) return
        if (!selected || !current) {
          setPlanDiverged(null)
          return
        }
        setPlanDiverged(selected.yamlContent !== current.yamlContent)
      })
      .catch(() => {
        // Best-effort warning — on failure leave it null so the
        // callout is omitted rather than asserting (no divergence)
        // we can't actually verify.
        if (!cancelled) setPlanDiverged(null)
      })
    return () => {
      cancelled = true
    }
  }, [open, testCaseId, revision.fullSha, currentSha, revision.phase, revision.sha])

  const subject = message.trim()
  const canSubmit = subject.length > 0 && !restoring

  async function handleSubmit() {
    if (!canSubmit) return
    await onSubmit({
      message: subject,
      details: details.trim() || undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Restore this version?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Replaces the current script with the selected version. The current
            code is preserved in history — restore creates a new commit on top.
          </p>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Restoring from</Label>
            <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs px-1.5 py-0.5 rounded bg-muted">
                  {PHASE_LABEL[revision.phase]}
                </span>
                <code className="text-xs font-mono">{revision.sha}</code>
                <span className="text-xs text-muted-foreground">
                  · {formatLocalDateTime(revision.date, tz)}
                </span>
              </div>
              <div className="text-sm">{revision.message}</div>
            </div>
          </div>

          {planDiverged === true && (
            <div
              className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200 flex gap-2"
              role="alert"
            >
              <AlertTriangle className="h-4 w-4 flex-none mt-0.5" />
              <div>
                This revision's English plan also differs from the current one.
                Restore will only replace the script — the plan stays at its
                current version. Edit Test Case manually if you also want to
                roll the plan back.
              </div>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Author</Label>
            <div className="text-sm">
              {author.name}{' '}
              <span className="text-muted-foreground">&lt;{author.email}&gt;</span>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="restore-message">
              Commit message <span className="text-destructive">*</span>
            </Label>
            <Input
              id="restore-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault()
                  void handleSubmit()
                }
              }}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="restore-details">Details (optional)</Label>
            <Textarea
              id="restore-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Why the rollback, or additional context"
              rows={3}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={restoring}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {restoring ? 'Restoring…' : 'Restore'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
